import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Send, X } from 'lucide-react';
import UserModalSelect from './UserModalSelect';
import DateCurrentInput from '../inputs/DateCurrentInput';
import { useMachineRepairRequests } from '../hooks/useMachineRepairRequests';
import { useToast } from '../context/ToastContext';
import { machineRepairUserLabel } from '../types/machineRepairRequests';
import type { MachineRepairThread } from '../types/machineRepairRequests';

interface MachineEntry {
  id: string;
  name: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  rnWorkOrderId: string;
  rnWorkOrderNumber?: string;
  subprocessId: string;
  subprocessName?: string;
  machines: MachineEntry[];
  /** When opened from Team Plan — skip WO-gated list fetch and show these threads. */
  seedThreads?: MachineRepairThread[];
}

const formatDateTime = (value?: string) => (value ? moment(value).format('DD.MM.YYYY HH:mm') : '');
const formatDateOnly = (value?: string | null) => (value && moment(value).isValid() ? moment(value).format('DD.MM.YYYY') : '');
const parseDDMMYYYYToISO = (value: string): string | undefined => {
  const parsed = moment(value.trim(), 'DD.MM.YYYY', true);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined;
};

const MachineRepairRequestModal: React.FC<Props> = ({ isOpen, onClose, rnWorkOrderId, rnWorkOrderNumber, subprocessId, subprocessName, machines, seedThreads }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const {
    threads,
    loading,
    nextNumber,
    loadThreads,
    createThread,
    addMessage,
    closeThread,
    refreshNextNumber,
    hydrateThreads,
  } = useMachineRepairRequests();
  const [recipients, setRecipients] = useState('');
  const [text, setText] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [replyText, setReplyText] = useState<Record<string, string>>({});
  const [replyDateMode, setReplyDateMode] = useState<Record<string, 'accept' | 'new'>>({});
  const [replyDateValue, setReplyDateValue] = useState<Record<string, string>>({});

  useEffect(() => {
    setRecipients('');
    setText('');
    setDateTo('');
    if (!isOpen) return;
    if (seedThreads && seedThreads.length > 0) {
      hydrateThreads(seedThreads, seedThreads[0]?.number || '');
      return;
    }
    if (rnWorkOrderId && subprocessId) {
      const filterMachineId = machines.length === 1 ? machines[0].id : '';
      void loadThreads({ rnWorkOrder: rnWorkOrderId, subprocessId, machineId: filterMachineId });
      void refreshNextNumber(rnWorkOrderId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, rnWorkOrderId, subprocessId, machines, seedThreads]);

  if (!isOpen) return null;

  const recipientIds = Array.from(new Set(recipients.split(',').map((id) => id.trim()).filter(Boolean)));

  // Subject: single machine → its name; multiple → "N machines".
  const subject = machines.length === 1
    ? machines[0].name.trim()
    : machines.length > 1
      ? `${machines.length} ${t('machineRepairRequests.machinesCount')}`
      : '';

  const canSubmit = machines.length > 0 && subject !== '' && recipientIds.length > 0 && !submitting && !!rnWorkOrderId && !!subprocessId;
  const displayNumber = nextNumber || (rnWorkOrderNumber ? `${rnWorkOrderNumber}/MAIN/01` : '');

  // Header label for the modal.
  const headerMachineLabel = machines.length === 1
    ? machines[0].name
    : machines.length > 1
      ? `${machines.length} ${t('machineRepairRequests.machinesCount')}`
      : '';

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    let successCount = 0;
    for (const machine of machines) {
      const requestContext = [
        `${t('rnMachineRepairs.machine')}: ${machine.name}`,
        `RN: ${rnWorkOrderNumber || rnWorkOrderId}`,
        `${t('rnMachineRepairs.title')}: ${subprocessName || subprocessId}`,
      ].join('\n');
      const requestText = [requestContext, text.trim()].filter(Boolean).join('\n\n');
      const created = await createThread({
        rnWorkOrder: rnWorkOrderId,
        subprocessId,
        machineId: machine.id,
        subject: machine.name,
        priority: 'medium',
        recipients: recipientIds,
        text: requestText,
        dateTo: parseDDMMYYYYToISO(dateTo) || null,
      });
      if (created) {
        successCount++;
        void refreshNextNumber(rnWorkOrderId);
      }
    }
    setSubmitting(false);
    if (successCount > 0) {
      setRecipients('');
      setText('');
      setDateTo('');
      if (successCount > 1) {
        showToast(`${t('machineRepairRequests.sent')} (${successCount} ${t('machineRepairRequests.machinesCount')})`, 'success');
      } else {
        const numberLabel = nextNumber ? ` (${nextNumber})` : '';
        showToast(`${t('machineRepairRequests.sent') || 'Request sent'}${numberLabel}`, 'success');
      }
    }
  };

  const handleModalClose = () => {
    setRecipients('');
    setText('');
    setDateTo('');
    onClose();
  };

  const handleReply = async (threadId: string) => {
    const value = (replyText[threadId] || '').trim();
    if (!value) return;
    const mode = replyDateMode[threadId];
    const newDateIso = mode === 'new' ? parseDDMMYYYYToISO(replyDateValue[threadId] || '') : undefined;
    const updated = await addMessage(threadId, value, newDateIso);
    if (updated) {
      setReplyText((prev) => ({ ...prev, [threadId]: '' }));
      setReplyDateMode((prev) => ({ ...prev, [threadId]: 'accept' }));
      setReplyDateValue((prev) => ({ ...prev, [threadId]: '' }));
    }
  };

  const handleClose = async (threadId: string) => {
    const updated = await closeThread(threadId);
    if (updated) showToast(t('machineRepairRequests.closedSuccess') || 'Closed', 'success');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <div className="min-w-0 pr-3">
            <div className="text-sm font-semibold text-blue-700">
              {displayNumber || t('machineRepairRequests.title')}
            </div>
            <h2 className="truncate text-xs font-medium text-gray-600">
              {t('machineRepairRequests.title')}
              {headerMachineLabel ? ` — ${headerMachineLabel}` : ''}
            </h2>
          </div>
          <button type="button" onClick={handleModalClose} className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50" aria-label={t('machineRepairRequests.close')}>
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-4">
          <div className="space-y-3 rounded-md border border-gray-200 p-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-700">{t('machineRepairRequests.requestNumber')}</label>
              <div className="flex h-9 items-center rounded-md border border-blue-200 bg-blue-50 px-3 text-sm font-semibold text-blue-800">
                {displayNumber || '—'}
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-700">{t('machineRepairRequests.subject')} <span className="text-red-500">*</span></label>
              <input value={subject} readOnly className="h-9 w-full rounded-md border border-gray-300 bg-gray-50 px-3 text-sm text-gray-700 focus:outline-none focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50" />
            </div>
            <div className="max-w-xs">
              <label className="mb-1 block text-xs font-semibold text-gray-700">{t('machineRepairRequests.dueDate')}</label>
              <DateCurrentInput
                value={dateTo}
                onChange={setDateTo}
                showIcon
                inputClassName="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-800 bg-white focus:outline-none focus:bg-blue-50 focus:ring-2 focus:ring-blue-500/50"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-700">{t('machineRepairRequests.recipients')} <span className="text-red-500">*</span></label>
              <UserModalSelect value={recipients} onChange={setRecipients} placeholder={t('machineRepairRequests.recipients') || undefined} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-700">{t('machineRepairRequests.text')}</label>
              <textarea
                value={text}
                onChange={(event) => setText(event.target.value)}
                rows={4}
                placeholder={t('machineRepairRequests.text') || undefined}
                className="w-full resize-y rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50"
              />
            </div>
            <div className="flex justify-end">
              <button type="button" onClick={handleSubmit} disabled={!canSubmit} className={`inline-flex h-9 items-center gap-2 rounded-md px-4 text-xs font-semibold transition-colors ${canSubmit ? 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/50' : 'cursor-not-allowed bg-gray-200 text-gray-400'}`}>
                <Send size={15} />{submitting ? '...' : t('machineRepairRequests.send')}
              </button>
            </div>
          </div>

          <>
            {loading && <div className="text-center text-xs text-gray-500">{t('common.loading') || 'Loading...'}</div>}
            {!loading && threads.length === 0 && (
              <div className="py-6 text-center text-xs text-gray-500">{t('machineRepairRequests.noThreads')}</div>
            )}
            {threads.map((thread) => (
              <div key={thread._id} className="rounded-md border border-gray-200">
                <div className="flex items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2">
                  <div className="min-w-0">
                    {thread.number && (
                      <div className="text-[11px] font-semibold text-blue-700">{thread.number}</div>
                    )}
                    <div className="text-xs font-semibold text-gray-800">{thread.subject}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-medium text-gray-500">{t('machineRepairRequests.dueDate')}: {formatDateOnly(thread.dateTo) || '—'}</span>
                    <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700">{t(`machineRepairRequests.priority_${thread.priority}`)}</span>
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">{t(`machineRepairRequests.status_${thread.status}`)}</span>
                    {thread.status !== 'closed' && (
                      <button type="button" onClick={() => handleClose(thread._id)} className="rounded border border-blue-200 bg-white px-2 py-0.5 text-[10px] font-medium text-blue-700 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500/50">
                        {t('machineRepairRequests.closeThread')}
                      </button>
                    )}
                  </div>
                </div>
                <div className="space-y-2 px-3 py-2">
                  {thread.messages.map((msg) => (
                    <div key={msg._id} className="rounded bg-gray-50 px-2 py-1.5 text-xs">
                      <div className="flex items-center justify-between text-[10px] text-gray-500">
                        <span className="font-semibold text-gray-700">{msg.authorName || machineRepairUserLabel(msg.author)}</span>
                        <span>{t(`machineRepairRequests.kind_${msg.kind}`)} · {formatDateTime(msg.createdAt)}</span>
                      </div>
                      <div className="mt-0.5 whitespace-pre-wrap text-gray-800">{msg.text}</div>
                    </div>
                  ))}
                </div>
                {thread.status !== 'closed' && (
                  <div className="space-y-2 border-t border-gray-200 px-3 py-2">
                    <div className="flex flex-wrap gap-2">
                      {thread.dateTo && (
                        <button
                          type="button"
                          onClick={() => setReplyDateMode((prev) => ({ ...prev, [thread._id]: 'accept' }))}
                          className={`rounded-md border px-2 py-1 text-[10px] font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${
                            (replyDateMode[thread._id] || 'accept') === 'accept'
                              ? 'border-blue-500 border-l-4 bg-blue-50 text-blue-700'
                              : 'border-gray-200 bg-white text-gray-600 hover:bg-blue-50/60'
                          }`}
                        >
                          {t('communication.acceptDeadline')}
                          <span className="ml-1 font-normal text-gray-500">({formatDateOnly(thread.dateTo)})</span>
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setReplyDateMode((prev) => ({ ...prev, [thread._id]: 'new' }))}
                        className={`rounded-md border px-2 py-1 text-[10px] font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${
                          replyDateMode[thread._id] === 'new'
                            ? 'border-blue-500 border-l-4 bg-blue-50 text-blue-700'
                            : 'border-gray-200 bg-white text-gray-600 hover:bg-blue-50/60'
                        }`}
                      >
                        {t('communication.setNewDeadline')}
                      </button>
                    </div>
                    {replyDateMode[thread._id] === 'new' && (
                      <div className="max-w-[10rem]">
                        <DateCurrentInput
                          value={replyDateValue[thread._id] || ''}
                          onChange={(value) => setReplyDateValue((prev) => ({ ...prev, [thread._id]: value }))}
                          showIcon
                          inputClassName="w-full rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-800 bg-white focus:outline-none focus:bg-blue-50 focus:ring-2 focus:ring-blue-500/50"
                        />
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <input
                        value={replyText[thread._id] || ''}
                        onChange={(e) => setReplyText((prev) => ({ ...prev, [thread._id]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleReply(thread._id); } }}
                        placeholder={t('machineRepairRequests.replyPlaceholder') || ''}
                        className="h-8 flex-1 rounded-md border border-gray-300 px-2 text-xs focus:outline-none focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50"
                      />
                      <button type="button" onClick={() => void handleReply(thread._id)} disabled={!(replyText[thread._id] || '').trim()} className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400">
                        {t('machineRepairRequests.reply')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </>
        </div>
      </div>
    </div>
  );
};

export default MachineRepairRequestModal;
